import {
  Checkbox,
  CircularProgress,
  Divider,
  List,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { organizationsApi } from "api";
import { getTranslation } from "common";
import { useSnackbar, useTranslations } from "hooks";
import { AssociatedUser, User } from "./UserItem";
import UserAvatar from "./UserAvatar";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  getResourceUsers,
  updateResourceUserAssociation,
  UserAssociationResource,
} from "utils/resourceUserAssociations";

interface ResourceUsersEditorProps {
  resource: UserAssociationResource;
  resourceId?: number | null;
  organizationId?: number | null;
  disabled?: boolean;
  onUpdate?: () => void;
}

const getUserName = (user: Partial<User | AssociatedUser>) =>
  [user.firstname, user.lastname].filter(Boolean).join(" ") ||
  user.email ||
  "";

const ResourceUsersEditor: React.FC<ResourceUsersEditorProps> = ({
  resource,
  resourceId,
  organizationId,
  disabled,
  onUpdate,
}) => {
  const translations = useTranslations();
  const { sendSnack } = useSnackbar();
  const [users, setUsers] = useState<User[]>([]);
  const [associatedUsers, setAssociatedUsers] = useState<AssociatedUser[]>([]);
  const [loading, setLoading] = useState(false);
  const [updatingUserId, setUpdatingUserId] = useState<number | null>(null);

  const associatedUserIds = useMemo(
    () => new Set(associatedUsers.map((user) => user.id)),
    [associatedUsers]
  );

  const refreshUsers = useCallback(async () => {
    if (!organizationId || !resourceId) {
      setUsers([]);
      setAssociatedUsers([]);
      return;
    }

    setLoading(true);
    try {
      const [organizationUsersResponse, associatedUsersResponse] =
        await Promise.all([
          organizationsApi.get(`/${organizationId}/users`),
          getResourceUsers(resource, resourceId),
        ]);
      setUsers(organizationUsersResponse.data.users || []);
      setAssociatedUsers(associatedUsersResponse.data.users || []);
    } catch {
      setUsers([]);
      setAssociatedUsers([]);
    } finally {
      setLoading(false);
    }
  }, [organizationId, resource, resourceId]);

  useEffect(() => {
    refreshUsers();
  }, [refreshUsers]);

  const toggleAssociation = async (userId: number, associated: boolean) => {
    if (!resourceId || disabled) return;

    setUpdatingUserId(userId);
    try {
      const response = await updateResourceUserAssociation({
        resource,
        resourceId,
        userId,
        associated,
      });
      sendSnack({
        message: `${response.data.responsemessage}`,
        type: "success",
      });
      await refreshUsers();
      onUpdate?.();
    } catch {
    } finally {
      setUpdatingUserId(null);
    }
  };

  if (!resourceId) {
    return null;
  }

  return (
    <Stack spacing={1.5} sx={{ mt: 2, minHeight: 0 }}>
      <Divider />
      <Typography variant="subtitle2" fontWeight={700}>
        {getTranslation(translations, "users.name")}
      </Typography>
      {loading ? (
        <Stack alignItems="center" sx={{ py: 2 }}>
          <CircularProgress size={24} />
        </Stack>
      ) : (
        <List
          dense
          disablePadding
          sx={{
            maxHeight: 280,
            overflow: "auto",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
          }}
        >
          {users.map((user) => {
            const checked = associatedUserIds.has(user.id);
            const updating = updatingUserId === user.id;

            return (
              <ListItem key={`resource-user-${resource}-${user.id}`} disablePadding>
                <ListItemButton
                  id={`${resource}-user-association-${user.id}`}
                  disabled={disabled || updating}
                  onClick={() => toggleAssociation(user.id, !checked)}
                >
                  <ListItemAvatar>
                    <UserAvatar user={user} />
                  </ListItemAvatar>
                  <ListItemText
                    primary={getUserName(user)}
                    secondary={user.email}
                    primaryTypographyProps={{ fontWeight: 600 }}
                  />
                  <Checkbox
                    edge="end"
                    checked={checked}
                    disabled={disabled || updating}
                    tabIndex={-1}
                  />
                </ListItemButton>
              </ListItem>
            );
          })}
        </List>
      )}
    </Stack>
  );
};

export default ResourceUsersEditor;
